import {
  ChatPanelDrawer,
  ClearAllChatHistory,
  AutoDeleteMessagesCard,
  MemberInfoCard,
  PinnedCard,
  GroupCommonCard,
  ButtonRed,
  ConfirmDialog,
  DeleteChatIcon,
  ReportCard,
} from '@halo/ui-chat-react';
import { Box, Stack } from '@chakra-ui/react';
import {
  GroupMemberJoinMode,
  type Session,
  useAppStore,
  type SessionStore,
} from '@halo/chat-sdk-react';
import { useNavigate } from 'react-router';
import { useShallowEnhance } from '@halo/zustand-enhance';
import Paths from '@/configs/router/paths';
import { usePortalContainer } from '@/scripts/hooks';
import { type ActionsStore, useActionsStore } from '@/store/actions';
import { userApis } from '@/configs/apis/user';
import { MuteNotificationsCardPlus } from './MuteNotificationsCardPlus';

export interface ChatSettingDrawerProps {
  sessionStore: SessionStore;
  open: boolean;
  setOpenChatSettingDrawer: (open: boolean) => void;
  fetchProfileGroup?: ActionsStore['fetchProfileGroup'];
  hideSession: (session_id: Session['chat_id'], is_delete?: boolean) => Promise<void>;
}

export const ChatSettingDrawer = memo(function ChatSettingDrawer({
  sessionStore,
  open,
  setOpenChatSettingDrawer,
  fetchProfileGroup,
  hideSession,
}: ChatSettingDrawerProps) {
  const { t } = useTranslation();
  const { session } = sessionStore;
  const client = useAppStore((state) => state.client);
  const user = useAppStore((state) => state.user);
  const updateSession = useAppStore((state) => state.updateSession);
  const { fetchProfileUser, setGroupCreateIsOpen, setClearChatHistoryIsOpen, setReportIsOpen } =
    useActionsStore(
      useShallowEnhance([
        'fetchProfileUser',
        'setGroupCreateIsOpen',
        'setClearChatHistoryIsOpen',
        'setReportIsOpen',
      ])
    );
  const navigate = useNavigate();
  const onClearAllChatHistory_ = useCallback(() => {
    setClearChatHistoryIsOpen(true);
  }, [setClearChatHistoryIsOpen]);

  const isMe = useMemo(() => {
    return sessionStore?.session?.user_info?.user_id === user?.user_id;
  }, [sessionStore?.session?.user_info?.user_id, user?.user_id]);

  const onDurationChange = useCallback(
    async (duration: number) => {
      const chat_id = session!.chat_id!;
      await client?.Api.session.setSessionExpireDuration(chat_id, BigInt(duration));
      updateSession({
        chat_id,
        expire_duration: duration,
      });
    },
    [client?.Api.session, session, updateSession]
  );

  const handleGroupClick = useCallback(
    (gid: string) => {
      fetchProfileGroup?.(gid, GroupMemberJoinMode.Search, true);
    },
    [fetchProfileGroup]
  );

  const { data: userGroups, run: runUserGroups } = useRequest(userApis.getUserGroupRelation, {
    manual: true,
  });

  useEffect(() => {
    if (!open) return;
    if (!sessionStore.session?.user_info?.user_id) return;
    runUserGroups(sessionStore.session.user_info.user_id);
  }, [open, runUserGroups, sessionStore.session?.user_info?.user_id]);

  const [deleteOpen, setDeleteOpen] = useState<boolean>(false);
  const { portalContainerRef } = usePortalContainer();
  const handleDelete = useCallback(async () => {
    await hideSession(sessionStore.session?.chat_id ?? '', true);
    setDeleteOpen(false);
    setOpenChatSettingDrawer(false);
    navigate(Paths.MESSAGES_CHATS);
  }, [hideSession, navigate, sessionStore.session?.chat_id, setOpenChatSettingDrawer]);

  const handleReport = useCallback(() => {
    setReportIsOpen(true);
  }, [setReportIsOpen]);

  return (
    <ChatPanelDrawer
      title={t('chatSettings.chatSettingsTitle')}
      open={open}
      onOpenChange={setOpenChatSettingDrawer}
    >
      <Stack gap="2">
        <MemberInfoCard
          avatar={session?.avatar ?? ''}
          name={session?.name ?? ''}
          userid={session?.user_info?.username ?? ''}
          onAddMember={() => {
            if (!session?.user_info) return;
            setGroupCreateIsOpen(true, [session?.user_info]);
          }}
          onAvatarClick={() => {
            fetchProfileUser(session?.user_info?.user_id ?? '', true);
          }}
        />
        <AutoDeleteMessagesCard value={session?.expire_duration} onSelect={onDurationChange} />
        <PinnedCard session={session} />
        {!isMe && (
          <>
            <MuteNotificationsCardPlus session={session} />
            <GroupCommonCard userGroups={userGroups?.data?.groups} onClick={handleGroupClick} />
          </>
        )}
        <ClearAllChatHistory onClearAllChatHistory={onClearAllChatHistory_} />
        {!isMe && <ReportCard handleReport={handleReport} />}
      </Stack>
      <Stack gap="2" p="4" pt="5" fontSize="sm">
        <ButtonRed
          variant="outline"
          borderRadius="xl"
          gap="1"
          onClick={() => {
            setDeleteOpen(true);
          }}
        >
          <DeleteChatIcon />
          {t('buttons.delete')}
        </ButtonRed>
        <ConfirmDialog
          disabledPortal={false}
          portalContainerRef={portalContainerRef}
          open={deleteOpen}
          onClose={() => setDeleteOpen(false)}
          title={t('chat.chatDeleteTitle')}
          onOk={handleDelete}
          okText={t('buttons.clear')}
        >
          <Box fontSize="sm">{t('chat.chatHistoryClearedOnDevice')}</Box>
        </ConfirmDialog>
      </Stack>
    </ChatPanelDrawer>
  );
});
